/**
 * Route table and role-aware navigation.
 *
 * `roles` decides what the sidebar *offers*; it is not authorization. Every route still renders
 * against the backend's answer, and every command is authorized server-side against the
 * authorization matrix (a viewer who types an issuance URL gets the backend's 403, rendered as
 * the forbidden state, not a blank screen).
 */

import type { InternalRole } from '../api/types';
import type { IconName } from '../ui/Icon';

export const ROUTES = {
  dashboard: '/',
  assets: '/assets',
  templates: '/templates',
  templateNew: '/templates/new',
  designer: '/templates/:templateId/designer',
  templateDetail: '/templates/:templateId',
  releaseDetail: '/templates/:templateId/releases/:releaseId',
  issue: '/issue',
  batches: '/batches',
  batchDetail: '/batches/:batchId',
  credentials: '/credentials',
  credentialDetail: '/credentials/:credentialId',
  settings: '/settings',
  users: '/settings/users',
  login: '/login',
  publicCredential: '/c/:code',
  claim: '/claim/:token',
  verify: '/verify',
} as const;

export function credentialPath(credentialId: string): string {
  return `/credentials/${credentialId}`;
}
export function templatePath(templateId: string): string {
  return `/templates/${templateId}`;
}
export function designerPath(templateId: string): string {
  return `/templates/${templateId}/designer`;
}
export function releasePath(templateId: string, releaseId: string): string {
  return `/templates/${templateId}/releases/${releaseId}`;
}
export function batchPath(batchId: string): string {
  return `/batches/${batchId}`;
}

export interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
  /** Roles for which the entry is offered; the backend remains authoritative. */
  roles: InternalRole[];
  /** Also mark active for these path prefixes. */
  match?: string[];
}

export interface NavGroup {
  label?: string;
  entries: NavEntry[];
}

/**
 * Role sets taken from the authorization matrix, per entry's *read* command — an entry is
 * offered only when the role can at least look at the screen behind it.
 */
const ALL: InternalRole[] = ['owner', 'admin', 'operator', 'viewer'];
/** `import.create` / `import.validate`: allowed for owner, admin, operator; denied for viewer. */
const IMPORTERS: InternalRole[] = ['owner', 'admin', 'operator'];
/** `issuer.read`, `audit.read`, `telemetry.read`, `integration_settings.read`: operator denied. */
const READERS: InternalRole[] = ['owner', 'admin', 'viewer'];
const ADMINS: InternalRole[] = ['owner', 'admin'];
/** `user.create` / `user.assign_role` / `user.disable`: owner only. */
const OWNERS: InternalRole[] = ['owner'];

/**
 * The sidebar IS the prototype's, and only the prototype's.
 *
 * Six entries in the reference's own order and wording, with its own `Ayarlar` label before the
 * last one (`#adminShell .nav`, the frozen prototype). §6 of the brief is explicit that `Kişiler`,
 * `Denetim Kaydı` and `Kullanıcılar` — all three added after the prototype was frozen — must not
 * appear as main menu rows, and equally explicit that their FUNCTIONS are not lost:
 *
 *   - recipient and organisation facts belong to the credential and its detail, where the
 *     subject snapshot and the issued record already carry them;
 *   - the credential's audit timeline belongs to the credential detail's «Denetim kaydı» block;
 *   - user management, being owner-only (`user.read`, `user.activate` — PO 7.5 §2.1, PO 7.6),
 *     is a surface of the settings area, offered to an owner and to nobody else.
 *
 * `ROUTES.users` therefore still exists and is still routed; it simply has no sidebar row. The
 * backend's `GET /subjects` and `GET /audit` are likewise untouched — what changed is the visible
 * information architecture, which the approved prototype owns.
 */
export const NAV: NavGroup[] = [
  {
    entries: [
      { to: ROUTES.dashboard, label: 'Panel', icon: 'dashboard', roles: ALL },
      { to: ROUTES.assets, label: 'Görsel Kütüphanesi', icon: 'assets', roles: ALL },
      {
        to: ROUTES.templates,
        label: 'Şablonlar',
        icon: 'templates',
        roles: ALL,
      },
      { to: ROUTES.batches, label: 'Toplu Üretim', icon: 'import', roles: IMPORTERS },
      {
        to: ROUTES.credentials,
        label: 'Rozet ve Sertifikalar',
        icon: 'credentials',
        roles: ALL,
        match: [ROUTES.issue],
      },
    ],
  },
  {
    label: 'Ayarlar',
    entries: [{ to: ROUTES.settings, label: 'E-posta ve Doğrulama', icon: 'settings', roles: READERS }],
  },
];

export const ROLE_GROUPS = { ALL, IMPORTERS, READERS, ADMINS, OWNERS };
